import HttpClient from 'services/HttpClient'

/** Product asset form (per brand + asset type + language) – used for products/assets add/edit */
export interface IProductAssetFormDto {
  id: string
  name: string
  assetType: { id: string; name: string }
  brand: { id: string; name: string }
  lang: string | null
  formFields?: IProductFormFieldDto[]
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

/** Product form field (per asset form) */
export interface IProductFormFieldDto {
  id: string
  name: string
  type: string
  label: string
  required: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface ICreateProductAssetFormRequest {
  assetTypeId: string
  name: string
  brandId: string
  /** Country code for lang-specific form; omit or null for default form (all countries) */
  lang?: string | null
  required?: boolean
}

export interface IUpdateProductAssetFormRequest {
  name?: string
  /** Country code or null to make the form the default */
  lang?: string | null
}

export interface ICreateProductFormFieldRequest {
  assetFormId: string
  name: string
  type: string
  label: string
  required?: boolean
}

export interface IUpdateProductFormFieldRequest {
  name?: string
  type?: string
  label?: string
  required?: boolean
}

export class AssetFormRepository {
  api: typeof HttpClient

  constructor(api: typeof HttpClient) {
    this.api = api
  }

  /** Get form (with formFields) by brand, asset type, lang – e.g. asset-forms/brand/:brandId/asset-type/:assetTypeId/lang/:lang */
  async getAssetFroms(
    brandGuid: string,
    assetTypeGuid: string,
    lang: string
  ): Promise<any> {
    return await this.api.get(
      `/asset-forms/brand/${brandGuid}/asset-type/${assetTypeGuid}/lang/${lang}`
    )
  }

  /** Get default form (lang: null) for brand + asset type. Uses path .../lang/default; backend should return the form where lang is null. */
  async getAssetFormDefault(
    brandGuid: string,
    assetTypeGuid: string
  ): Promise<any> {
    return await this.api.get(
      `/asset-forms/brand/${brandGuid}/asset-type/${assetTypeGuid}/lang/default`
    )
  }

  /** List all product asset forms */
  getAssetFormsAll(): Promise<any> {
    return this.api.get('/asset-forms/all')
  }

  getAssetFormById(id: string): Promise<any> {
    return this.api.get(`/asset-forms/${id}`)
  }

  createAssetForm(payload: ICreateProductAssetFormRequest): Promise<any> {
    return this.api.post('/asset-forms', payload)
  }

  updateAssetForm(
    id: string,
    payload: IUpdateProductAssetFormRequest
  ): Promise<any> {
    return this.api.patch(`/asset-forms/${id}`, payload)
  }

  deleteAssetForm(id: string): Promise<any> {
    return this.api.delete(`/asset-forms/${id}`)
  }

  /** List form fields for a product asset form */
  getFormFieldsByAssetFormId(assetFormId: string): Promise<any> {
    return this.api.get(`/form-fields/asset-form/${assetFormId}`)
  }

  createFormField(payload: ICreateProductFormFieldRequest): Promise<any> {
    return this.api.post('/form-fields', payload)
  }

  updateFormField(
    id: string,
    payload: IUpdateProductFormFieldRequest
  ): Promise<any> {
    return this.api.patch(`/form-fields/${id}`, payload)
  }

  deleteFormField(id: string): Promise<any> {
    return this.api.delete(`/form-fields/${id}`)
  }
}

 
export default new AssetFormRepository(HttpClient)
